import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Activity", href: "#procurement-activity" },
  { label: "Workflow", href: "#integration" },
  { label: "Outcomes", href: "#outcomes" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 lg:h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center group"
        >
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-[#0B1F3A] text-[15px] tracking-tight">
              DeMaxtore
            </span>
            <span className="font-inter text-[10px] font-medium tracking-[0.18em] uppercase text-[#64748B]">
              CommodityBid™️
            </span>
          </div>
        </Link>

        <nav
          data-testid="navbar-links"
          className="hidden lg:flex items-center gap-1"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0B1F3A] rounded-lg hover:bg-slate-100/70 transition-all"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/demo"
            data-testid="navbar-secondary-cta"
            className="px-4 py-2 text-sm font-medium text-[#0B1F3A] hover:text-[#3B82F6] transition-colors whitespace-nowrap"
          >
            Book a Demo
          </Link>
          <Link
            to="/workspace"
            data-testid="navbar-primary-cta"
            className="group inline-flex items-center gap-1.5 bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-sm transition-all hover:shadow-md hover:translate-y-[-1px] whitespace-nowrap"
          >
            Launch CommodityBid™️
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
          className="lg:hidden h-10 w-10 rounded-lg flex items-center justify-center text-[#0B1F3A] hover:bg-slate-100"
          aria-label="Toggle navigation"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200/60 px-6 py-4 space-y-2"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-[#0B1F3A] rounded-lg hover:bg-slate-100"
            >
              {l.label}
            </a>
          ))}
          <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
            <Link
              to="/demo"
              data-testid="navbar-mobile-secondary-cta"
              className="px-3 py-2.5 text-sm font-medium text-[#0B1F3A] rounded-lg hover:bg-slate-100"
            >
              Book a Demo
            </Link>
            <Link
              to="/workspace"
              data-testid="navbar-mobile-primary-cta"
              className="px-3 py-2.5 text-sm font-medium text-white bg-[#0B1F3A] rounded-xl text-center"
            >
              Launch CommodityBid™️
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
